import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useLang } from "../context/LangContext";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Trash2 } from "lucide-react";

export default function AuditLog() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/audit-log");
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const clearAll = async () => {
    if (!window.confirm(t("audit.clearConfirm"))) return;
    try {
      await api.delete("/audit-log");
      toast.success(t("common.saved"));
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const removeOne = async (id) => {
    try {
      await api.delete(`/audit-log/${id}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
      <div className="flex items-end justify-between gap-3 mb-6">
        <div>
          <p className="label-mini">Admin</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight leading-none mt-1">{t("audit.title")}</h1>
        </div>
        {items.length > 0 && (
          <Button onClick={clearAll} variant="outline" className="border-destructive text-destructive hover:bg-red-50" style={{ borderRadius: 4 }} data-testid="audit-clear-button">
            <Trash2 className="w-4 h-4 mr-2" /> {t("audit.clear")}
          </Button>
        )}
      </div>

      <div className="swiss-card overflow-hidden">
        <div className="overflow-x-auto table-scroll">
          <table className="w-full text-sm" data-testid="audit-table">
            <thead className="bg-[#F8F9FA] border-b border-border">
              <tr>
                <th className="px-3 py-3 text-left font-semibold whitespace-nowrap">{t("audit.cols.timestamp")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("audit.cols.user")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("audit.cols.action")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("audit.cols.target")}</th>
                <th className="px-3 py-3 text-left font-semibold">{t("audit.cols.details")}</th>
                <th className="px-3 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && (
                <tr><td colSpan={6} className="p-8 text-center text-muted-foreground">{t("audit.empty")}</td></tr>
              )}
              {items.map((a) => (
                <tr key={a.id} className="border-b border-border last:border-0 hover:bg-[#F8F9FA]" data-testid={`audit-row-${a.id}`}>
                  <td className="px-3 py-3 text-muted-foreground whitespace-nowrap mono text-xs">
                    {a.timestamp && new Date(a.timestamp).toLocaleString(lang === "de" ? "de-DE" : "en-US")}
                  </td>
                  <td className="px-3 py-3">{a.user_name}</td>
                  <td className="px-3 py-3 mono text-xs">{a.action}</td>
                  <td className="px-3 py-3 mono text-xs text-muted-foreground">{a.target_type && `${a.target_type}: ${a.target_id || ""}`}</td>
                  <td className="px-3 py-3 text-muted-foreground">{a.details}</td>
                  <td className="px-3 py-3 text-right">
                    <button
                      onClick={() => removeOne(a.id)}
                      className="text-muted-foreground hover:text-destructive p-1"
                      aria-label={t("audit.delete")}
                      data-testid={`audit-delete-${a.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
